import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';

const OfflineContext = createContext(null);

// IndexedDB helpers
const DB_NAME = 'FaThutOfflineDB';
const DB_VERSION = 1;

const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      if (!db.objectStoreNames.contains('orders')) {
        db.createObjectStore('orders', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('customers')) {
        db.createObjectStore('customers', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('pendingSync')) {
        db.createObjectStore('pendingSync', { keyPath: 'id', autoIncrement: true });
      }
    };
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const storeData = async (storeName, data) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    
    if (Array.isArray(data)) {
      data.forEach(item => store.put(item));
    } else {
      store.put(data);
    }
    
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
};

const getData = async (storeName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const getDataById = async (storeName, id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const addPendingSync = async (data) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingSync', 'readwrite');
    const store = tx.objectStore('pendingSync');
    const request = store.add({
      ...data,
      timestamp: Date.now()
    });
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const getPendingSync = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingSync', 'readonly');
    const store = tx.objectStore('pendingSync');
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const clearPendingSync = async (id) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('pendingSync', 'readwrite');
    const store = tx.objectStore('pendingSync');
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Wieder online');
      syncPendingData();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Offline-Modus aktiv', {
        description: 'Änderungen werden gespeichert und später synchronisiert'
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js')
        .then(() => {
          // Service Worker registered successfully
        })
        .catch(() => {
          // Service Worker registration failed - offline features may not work
        });
      
      // Listen for messages from service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'SYNC_COMPLETE') {
          toast.success('Daten synchronisiert');
          loadPendingCount();
        }
        if (event.data.type === 'GET_TOKEN') {
          event.ports[0].postMessage({ token: localStorage.getItem('token') });
        }
      });
    }

    loadPendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPendingCount = async () => {
    try {
      const pending = await getPendingSync();
      setPendingCount(pending.length);
    } catch {
      // Silent error handling for offline context
    }
  };

  const syncPendingData = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    
    setSyncing(true);
    try {
      const pending = await getPendingSync();
      
      for (const item of pending) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(item.url, {
            method: item.method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: item.body ? JSON.stringify(item.body) : undefined
          });
          
          if (response.ok) {
            await clearPendingSync(item.id);
            toast.success('Offline-Daten synchronisiert');
          }
        } catch {
          // Will retry later
        }
      }
      
      loadPendingCount();
    } catch {
      // Silent sync error
    } finally {
      setSyncing(false);
    }
  }, [syncing]);

  // Cache orders for offline access
  const cacheOrders = useCallback(async (orders) => {
    try {
      await storeData('orders', orders);
    } catch {
      // Silent cache error
    }
  }, []);

  // Cache customers for offline access
  const cacheCustomers = useCallback(async (customers) => {
    try {
      await storeData('customers', customers);
    } catch {
      // Silent cache error
    }
  }, []);

  // Get cached orders
  const getCachedOrders = useCallback(async () => {
    try {
      return await getData('orders');
    } catch {
      return [];
    }
  }, []);

  // Get cached order by ID
  const getCachedOrder = useCallback(async (id) => {
    try {
      return await getDataById('orders', id);
    } catch {
      return null;
    }
  }, []);

  // Get cached customers
  const getCachedCustomers = useCallback(async () => {
    try {
      return await getData('customers');
    } catch {
      return [];
    }
  }, []);

  // Queue an action for later sync
  const queueForSync = useCallback(async (url, method, body) => {
    try {
      await addPendingSync({ url, method, body });
      loadPendingCount();
      return true;
    } catch {
      return false;
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    isOnline,
    pendingCount,
    syncing,
    cacheOrders,
    cacheCustomers,
    getCachedOrders,
    getCachedOrder,
    getCachedCustomers,
    queueForSync,
    syncPendingData
  }), [isOnline, pendingCount, syncing, cacheOrders, cacheCustomers, getCachedOrders, getCachedOrder, getCachedCustomers, queueForSync, syncPendingData]);

  return (
    <OfflineContext.Provider value={contextValue}>
      {children}
    </OfflineContext.Provider>
  );
};

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export default OfflineContext;
